<template>
  <Transition name="expand">
    <View wrap="wrap" column-gap="md" v-if="showComponent">
      <View align="center" gap="sm" v-for="(requirement, index) in requirements" :key="index">
        <div class="flip-card">
          <div class="flip-card-inner" :class="{ 'is-flipped': requirement.completed }">
            <div class="flip-card-front">
              <Icon class="requirement-icon pending" :name="'circle'" size="extra-small" />
            </div>
            <div class="flip-card-back">
              <Icon class="requirement-icon completed" :name="'check-bold'" size="extra-small" />
            </div>
          </div>
        </div>

        <Text variant="body-small" class="indicator-message" :class="{ completed: requirement.completed }">
          {{ requirement.message }}
        </Text>
      </View>
    </View>
  </Transition>
</template>

<script setup lang="ts">
import { reactive, watch } from 'vue';

import Icon from './icon.vue';
import { passwordRequirements } from './password-requirements';
import Text from './text.vue';
import View from './view/view.vue';

interface PasswordStrengthRequirement {
  regex: RegExp;
  message: string;
  completed: boolean;
}

const emit = defineEmits<{
  (event: 'password-status-change', valid: boolean): void;
}>();

const props = defineProps<{
  password: string;
  showComponent: boolean;
}>();

const requirements: PasswordStrengthRequirement[] = reactive(
  passwordRequirements.map((requirement) => ({ ...requirement, completed: false })),
);

const passwordMeetsRequirement = (requirement: PasswordStrengthRequirement): boolean => {
  return requirement.regex.test(props.password);
};

watch(
  () => props.password,
  () => {
    requirements.forEach((requirement) => {
      requirement.completed = passwordMeetsRequirement(requirement);
    });

    emit(
      'password-status-change',
      requirements.every((requirement) => requirement.completed),
    );
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.indicator-message {
  color: var(--t-color-neutral-500);
  transition: color 0.3s ease;

  &.completed {
    color: var(--t-color-neutral-700);
  }
}

.requirement-icon {
  --t-icon-size: var(--t-spacing-sm) !important;

  &.pending {
    color: var(--t-color-neutral-500);
  }

  &.completed {
    color: var(--t-color-green-500);
  }
}

.expand-enter-from {
  max-height: 0;
  opacity: 0;
}

.expand-enter-to {
  max-height: 5rem;
  opacity: 1;
}

.expand-enter-active {
  transition:
    max-height 0.6s ease,
    opacity 0.6s ease;
}

.flip-card {
  perspective: 1000px;
  width: 0.5rem;
  height: 1.7rem;
}

.flip-card-inner {
  position: relative;
  text-align: center;
  transition: transform 0.6s;
  transform-style: preserve-3d;
}

.flip-card-inner.is-flipped {
  transform: rotateY(180deg);
}

.flip-card-front,
.flip-card-back {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
}

.flip-card-back {
  transform: rotateY(180deg);
}
</style>
